import type { LunaClient } from '@vlabs/api-bindings';
import { apiContainer } from '@vlabs/api-bindings';
import { cloneDeep, set } from 'lodash';

import { handlerLivenessDeepfake } from '@vlabs/shared/payloads/handlers';
import { isVersionGte } from '@vlabs/shared/utils';

const imageModificationPolicy = {
  estimate: 1,
  threshold: 0,
};

interface TLunaVersion {
  major: number;
  minor: number;
  patch: number;
}

export interface EstimateLivenessDeepfakeProps {
  handlerId: string;
  file?: File;
  liveness?: boolean;
  deepfake?: boolean;
  lpVersion?: TLunaVersion;
  raw?: boolean;
  withImageModification?: boolean;
  withFaceQuality?: boolean;
  multifacePolicy?: 0 | 1 | 2;
}

export type EstimateLivenessDeepfakeReturn = Record<string, unknown>;

export async function estimateLivenessDeepfake({
  handlerId,
  file = undefined,
  liveness = false,
  deepfake = false,
  lpVersion = undefined,
  raw = false,
  withImageModification = true,
  withFaceQuality = true,
  multifacePolicy = 0,
}: EstimateLivenessDeepfakeProps): Promise<EstimateLivenessDeepfakeReturn> {
  // if (!file ?? !handlerId) return undefined;

  const isImageModificationAvailable = isVersionGte(lpVersion, { major: 5, minor: 101, patch: 0 });

  const requestBody = cloneDeep(handlerLivenessDeepfake);
  set(requestBody, 'policies.detect_policy.multiface_policy', multifacePolicy);
  set(requestBody, 'policies.detect_policy.face_quality.estimate', Number(withFaceQuality));
  if (liveness) {
    set(requestBody, 'policies.detect_policy.estimate_liveness', {
      estimate: 1,
      liveness_threshold: 0.5,
    });
  }
  if (deepfake) {
    set(requestBody, 'policies.detect_policy.estimate_deepfake', {
      estimate: 1,
      real_threshold: 0.5,
    });
  }
  if (withImageModification && isImageModificationAvailable) {
    set(requestBody, 'policies.detect_policy.face_quality.checks.image_modification', imageModificationPolicy);
  }

  const data = await (apiContainer.lunaClient as LunaClient).events.emit(handlerId, file, requestBody);

  if (raw) return data._raw;
  if (data.events?.length > 0) return data.events[0];
  return data;
}
